import { z } from 'zod';
import {
  AwsIamRoleArnSchema,
  AwsStsExternalIdSchema,
} from '@terra/shared-contracts-aws';
import { GcpProjectIdSchema } from '@terra/shared-contracts-gcp';
import {
  AiEmbeddingModelNameWireSchema,
  AiModelNameWireSchema,
} from '@terra/shared-schemas-orchestrator';

const AiAccountModelNameSchema = z.union([
  AiModelNameWireSchema,
  AiEmbeddingModelNameWireSchema,
]);

const NonemptyStringSchema = z.string().min(1);

export const AI_ACCOUNT_KINDS = [
  'bedrock',
  'vertex',
  'fireworks',
  'gateway',
] as const;

export const AiAccountKindSchema = z.enum(AI_ACCOUNT_KINDS);

export const AI_ACCOUNT_SOURCES = ['env', 'stored'] as const;

export const AiAccountSourceSchema = z.enum(AI_ACCOUNT_SOURCES);

export const AI_ACCOUNT_USAGE_OWNERS = ['terra', 'byoai'] as const;

export const AiAccountUsageOwnerSchema = z.enum(AI_ACCOUNT_USAGE_OWNERS);

export const AiAccountAudienceSchema = z.enum(['all', 'selected']);

export const AiAccountTenantScopeSchema = z
  .object({
    audience: AiAccountAudienceSchema,
    tenantIds: z.array(NonemptyStringSchema),
  })
  .strict()
  .refine(
    (scope) =>
      scope.audience === 'all'
        ? scope.tenantIds.length === 0
        : scope.tenantIds.length > 0,
    {
      message:
        'audience "all" must carry no tenant ids, audience "selected" must carry at least one',
    },
  );

export const GatewayApiFormatSchema = z.enum([
  'anthropic-messages',
  'bedrock-passthrough',
  'openai-chat-completions',
  'openai-responses',
]);

export const GatewayEmbeddingApiFormatSchema = z.enum([
  'bedrock-passthrough',
  'openai-chat-completions',
]);

export const GatewayAuthModeSchema = z.enum([
  'bearer-token',
  'api-key',
  'custom-headers-only',
]);

export const AI_ACCOUNT_BEDROCK_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-2',
  'ca-central-1',
  'eu-central-1',
  'eu-west-1',
  'eu-west-2',
] as const;

export const AI_ACCOUNT_VERTEX_LOCATIONS = [
  'global',
  'us-central1',
  'us-east1',
  'us-east4',
  'us-east5',
  'us-west1',
  'us-west4',
] as const;

export const AI_ACCOUNT_BEDROCK_SCOPES = [
  'in-region',
  'global',
  'us',
  'eu',
] as const;

export const AiAccountBedrockRegionSchema = z.enum(AI_ACCOUNT_BEDROCK_REGIONS);
export const AiAccountBedrockScopeSchema = z.enum(AI_ACCOUNT_BEDROCK_SCOPES);
export const AiAccountVertexLocationSchema = z.enum(
  AI_ACCOUNT_VERTEX_LOCATIONS,
);

export const BedrockAccountSettingsSchema = z
  .object({
    externalId: AwsStsExternalIdSchema.optional(),
    kind: z.literal('bedrock'),
    region: AiAccountBedrockRegionSchema,
    roleArn: AwsIamRoleArnSchema.optional(),
  })
  .strict();

export const VertexAccountSettingsSchema = z
  .object({
    kind: z.literal('vertex'),
    location: AiAccountVertexLocationSchema,
    projectId: GcpProjectIdSchema,
    serviceAccountEmail: z.email().optional(),
  })
  .strict();

export const FireworksAccountSettingsSchema = z
  .object({
    baseUrl: z.url(),
    kind: z.literal('fireworks'),
  })
  .strict();

/**
 * Endpoint and identity only. The wire format is declared per model on
 * `AiAccountModelSchema.apiFormat`, because one gateway serves several dialects
 * from the same base URL under the same credential.
 */
export const GatewayAccountSettingsSchema = z
  .object({
    authMode: GatewayAuthModeSchema,
    baseUrl: z.url(),
    kind: z.literal('gateway'),
  })
  .strict();

export const AiAccountSettingsSchema = z.discriminatedUnion('kind', [
  BedrockAccountSettingsSchema,
  VertexAccountSettingsSchema,
  FireworksAccountSettingsSchema,
  GatewayAccountSettingsSchema,
]);

export const FireworksAccountSecretSchema = z
  .object({
    apiKey: NonemptyStringSchema,
    kind: z.literal('fireworks'),
  })
  .strict();

export const GatewayAccountSecretSchema = z
  .object({
    apiKey: NonemptyStringSchema.optional(),
    bearerToken: NonemptyStringSchema.optional(),
    customHeaders: z.record(z.string(), z.string()).default({}),
    kind: z.literal('gateway'),
  })
  .strict();

export const AiAccountSecretSchema = z.discriminatedUnion('kind', [
  FireworksAccountSecretSchema,
  GatewayAccountSecretSchema,
]);

export const AiAccountProbeStatusSchema = z.enum(['failed', 'succeeded']);

export const AiAccountProbeResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('succeeded') }).strict(),
  z
    .object({
      error: z.string().optional(),
      status: z.literal('failed'),
    })
    .strict(),
]);

export const TerraAiGatewayCapabilitiesSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict();

export const AiAccountVerificationSchema = z
  .object({
    at: z.iso.datetime(),
    error: z.string().optional(),
    succeeded: z.boolean(),
  })
  .strict();

/**
 * One model an account offers. `region`, `scope` and `apiFormat` are all
 * provider-specific. `apiFormat` is the wire dialect this model speaks: it is
 * required for gateway accounts and unset for every other kind, where the
 * model registry declares the format per provider.
 */
export const AiAccountModelSchema = z
  .object({
    apiFormat: GatewayApiFormatSchema.optional(),
    enabled: z.boolean(),
    lastValidation: AiAccountVerificationSchema.optional(),
    modelId: NonemptyStringSchema.max(512),
    pricing: z.unknown().optional(),
    region: NonemptyStringSchema.optional(),
    scope: AiAccountBedrockScopeSchema.optional(),
    modelName: AiAccountModelNameSchema,
  })
  .strict();

export const AiAccountModelWriteSchema = AiAccountModelSchema.pick({
  apiFormat: true,
  modelId: true,
  pricing: true,
  region: true,
  scope: true,
  modelName: true,
}).strict();

/**
 * A gateway serves several wire dialects from one base URL, so the format is a
 * property of the model rather than the account. Every other kind takes its
 * format from the model registry, so carrying one on the model would be a
 * second source of truth.
 */
const GATEWAY_MODEL_API_FORMAT_MESSAGE =
  'gateway account models must declare an apiFormat, and models of every other kind must omit it';

const modelApiFormatMatchesKind = ({
  models,
  settings,
}: {
  models: readonly z.infer<typeof AiAccountModelSchema>[];
  settings: z.infer<typeof AiAccountSettingsSchema>;
}): boolean =>
  models.every((model) =>
    settings.kind === 'gateway'
      ? model.apiFormat !== undefined
      : model.apiFormat === undefined,
  );

export const AiAccountModelListSchema = z
  .object({ models: z.array(AiAccountModelSchema) })
  .strict();

export const AiAccountModelParamsSchema = z
  .object({ id: NonemptyStringSchema, modelName: AiAccountModelNameSchema })
  .strict();

export const ByoAiAccountModelParamsSchema = z
  .object({ modelName: AiAccountModelNameSchema })
  .strict();

export const AiAccountModelEnabledSchema = z
  .object({ enabled: z.boolean() })
  .strict();

const AiAccountBaseSchema = z.object({
  displayName: NonemptyStringSchema.max(120),
  enabled: z.boolean(),
  id: NonemptyStringSchema,
  lastVerification: AiAccountVerificationSchema.optional(),
  settings: AiAccountSettingsSchema,
});

export const TerraAiAccountSchema = AiAccountTenantScopeSchema.safeExtend({
  ...AiAccountBaseSchema.shape,
  hasSecret: z.boolean(),
  models: z.array(AiAccountModelSchema).default([]),
  source: AiAccountSourceSchema,
})
  .strict()
  .refine(modelApiFormatMatchesKind, {
    message: GATEWAY_MODEL_API_FORMAT_MESSAGE,
    path: ['models'],
  });

export const ByoAiAccountSchema = AiAccountBaseSchema.extend({
  hasSecret: z.boolean(),
  models: z.array(AiAccountModelSchema),
})
  .strict()
  .refine(modelApiFormatMatchesKind, {
    message: GATEWAY_MODEL_API_FORMAT_MESSAGE,
    path: ['models'],
  });

export const TerraAiAccountListSchema = z
  .object({
    accounts: z.array(TerraAiAccountSchema),
    terraAiGateway: TerraAiGatewayCapabilitiesSchema.default({
      enabled: false,
    }),
  })
  .strict();

const AiAccountWriteBaseSchema = z.object({
  displayName: NonemptyStringSchema.max(120),
  enabled: z.boolean().default(true),
  secret: AiAccountSecretSchema.optional(),
  settings: AiAccountSettingsSchema,
});

export const TerraAiAccountWriteSchema = AiAccountTenantScopeSchema.safeExtend({
  ...AiAccountWriteBaseSchema.shape,
}).strict();

export const ByoAiAccountWriteSchema = AiAccountWriteBaseSchema.extend(
  {},
).strict();

export const AiAccountIdParamsSchema = z
  .object({ id: NonemptyStringSchema })
  .strict();

export const AiAccountDeleteResponseSchema = z
  .object({ success: z.literal(true) })
  .strict();

export const ByoAiAccountResponseSchema = z
  .object({ account: ByoAiAccountSchema.nullable(), enabled: z.boolean() })
  .strict();

/**
 * The matrix travels with the credentials because inference needs both in the
 * same breath: which account to call, and which id that account calls it by.
 * One cached document answers both.
 */
export const AiAccountCredentialsSchema = z
  .object({
    accountId: NonemptyStringSchema,
    displayName: NonemptyStringSchema.max(120),
    encryptedSecret: NonemptyStringSchema.optional(),
    expiresAt: z.iso.datetime().optional(),
    models: z.array(AiAccountModelSchema).default([]),
    settings: AiAccountSettingsSchema,
  })
  .strict()
  .refine(modelApiFormatMatchesKind, {
    message: GATEWAY_MODEL_API_FORMAT_MESSAGE,
    path: ['models'],
  });

export const AiAccountCredentialsResponseSchema = z
  .object({ credentials: AiAccountCredentialsSchema.nullable() })
  .strict();

export const TerraAccountModelCacheDocumentSchema = z
  .object({
    accountId: NonemptyStringSchema,
    displayName: NonemptyStringSchema.max(120),
    encryptedSecret: NonemptyStringSchema.optional(),
    model: AiAccountModelSchema,
    owner: z.literal('terra').default('terra'),
    settings: AiAccountSettingsSchema,
  })
  .strict()
  .refine(
    ({ model, settings }) =>
      modelApiFormatMatchesKind({ models: [model], settings }),
    { message: GATEWAY_MODEL_API_FORMAT_MESSAGE, path: ['model'] },
  );

export const ResolvedByoAiAccountSchema = z
  .object({
    accountId: NonemptyStringSchema,
    displayName: NonemptyStringSchema.max(120),
    kind: AiAccountKindSchema,
    models: z.array(AiAccountModelSchema),
    settings: AiAccountSettingsSchema,
  })
  .strict()
  .refine(modelApiFormatMatchesKind, {
    message: GATEWAY_MODEL_API_FORMAT_MESSAGE,
    path: ['models'],
  });

/**
 * `absent` is a cache miss and must be refetched. `present` is a definitive
 * answer, where a null account means the tenant is managed by Terra — that
 * negative result is worth caching, since it is the common case.
 */
export const ResolvedByoAiAccountDocumentSchema = z.discriminatedUnion(
  'state',
  [
    z.object({ state: z.literal('absent') }).strict(),
    z
      .object({
        account: ResolvedByoAiAccountSchema.nullable(),
        state: z.literal('present'),
      })
      .strict(),
  ],
);
